import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, finalize, takeUntil } from 'rxjs';
import { MainCurrencyDto } from '../../../_core/_model/currency';
import { HouseTradeQuoteDto, TradeExecutionMode } from '../../../_core/_model/house-trade';
import { OrderDraftService, OrderPrefill } from '../../../_core/_services/order-draft-service';
import { FormatService } from '../../../_core/_services/format';
import { HouseTradeService } from '../../../_core/_services/house-trade.service';

export type OrderEntrySubmitBehavior = 'navigate' | 'emit';

export interface OrderEntryPairChange {
  fromCurrency: string;
  toCurrency: string;
}

export interface OrderEntrySubmission {
  draft: OrderPrefill;
  mode: TradeExecutionMode;
  quote: HouseTradeQuoteDto | null;
}

@Component({
  selector: 'app-order-entry',
  standalone: false,
  templateUrl: './order-entry.html',
  styleUrl: './order-entry.css'
})
export class OrderEntry implements OnInit, OnChanges, OnDestroy {
  @Input() title = 'Create Order';
  @Input() currencies: MainCurrencyDto[] = [];
  @Input() fee = 0.0;
  @Input() available: number | null = null;
  @Input() minimumHaveAmount = 0.01;
  @Input() maximumHaveAmount: number | null = null;
  @Input() loading = false;
  @Input() blockedMessage = '';
  @Input() errorMessage = '';
  @Input() errorButtonText = '';
  @Input() enableInstantQuotes = true;
  @Input() submitBehavior: OrderEntrySubmitBehavior = 'navigate';
  @Input() initialDraft: OrderPrefill | null = null;
  @Input() autoSubmitInitialDraft = false;
  @Input() submissionLocked = false;

  @Output() submitted = new EventEmitter<OrderEntrySubmission>();
  @Output() haveCurrencyChanged = new EventEmitter<string>();
  @Output() pairChanged = new EventEmitter<OrderEntryPairChange>();
  @Output() errorAction = new EventEmitter<void>();

  orderForm: FormGroup;
  tradeMode: TradeExecutionMode = 'instant';
  instantQuote: HouseTradeQuoteDto | null = null;
  quoteLoading = false;
  quoteExpiresInSeconds = 0;
  quoteRequestError = '';
  submissionPending = false;

  private readonly amountPattern = /^(?:\d+|\d*\.\d{1,2})$/;
  private readonly quoteRequest$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private updating = false;
  private quoteRequestSequence = 0;
  private quoteCountdownTimer?: ReturnType<typeof setInterval>;
  private initialDraftApplied = false;
  private autoSubmitPending = false;

  constructor(
    private fb: FormBuilder,
    private format: FormatService,
    private houseTradeService: HouseTradeService,
    private draftService: OrderDraftService,
    private router: Router
  ) {
    this.orderForm = this.fb.nonNullable.group({
      haveCurrency: ['', Validators.required],
      haveAmount: [0, [Validators.required, Validators.min(0.01), Validators.pattern(this.amountPattern)]],
      needCurrency: ['', Validators.required],
      needAmount: [0, [Validators.required, Validators.min(0.01), Validators.pattern(this.amountPattern)]],
      price: [0, [Validators.required, Validators.min(0.00000000001), Validators.pattern(/^(?:\d+|\d*\.\d{1,12})$/)]]
    });

    this.quoteRequest$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.requestInstantQuote());
  }

  get haveCurrency() { return this.orderForm.get('haveCurrency'); }
  get haveAmount() { return this.orderForm.get('haveAmount'); }
  get needCurrency() { return this.orderForm.get('needCurrency'); }
  get needAmount() { return this.orderForm.get('needAmount'); }
  get price() { return this.orderForm.get('price'); }

  get isInstantMode(): boolean {
    return this.tradeMode === 'instant';
  }

  get hasUsableInstantQuote(): boolean {
    return !!this.instantQuote?.quoteId &&
      this.instantQuote.instantAvailable &&
      !this.isInstantQuoteExpired;
  }

  get isInstantQuoteExpired(): boolean {
    const expiresAt = this.instantQuote?.expiresAt;
    return !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
  }

  get instantQuoteMessage(): string {
    if (!this.enableInstantQuotes) {
      return 'Continue to the Orders page to receive a fresh instant quote.';
    }
    if (this.quoteLoading) return 'Checking the latest instant rate and available liquidity...';
    if (this.quoteRequestError) return this.quoteRequestError;
    return this.instantQuote?.availabilityMessage ?? 'Enter an amount to request an instant quote.';
  }

  get haveOptions(): MainCurrencyDto[] {
    const need = this.needCurrency?.value;
    return this.currencies.filter(currency => currency.currencyCode !== need);
  }

  get needOptions(): MainCurrencyDto[] {
    const have = this.haveCurrency?.value;
    return this.currencies.filter(currency => currency.currencyCode !== have);
  }

  get canSubmit(): boolean {
    if (this.loading || this.blockedMessage || this.errorMessage ||
      this.submissionLocked || this.submissionPending) return false;

    const baseValid = !!this.haveCurrency?.value &&
      !!this.needCurrency?.value &&
      this.haveCurrency.value !== this.needCurrency.value &&
      !this.haveCurrency.invalid &&
      !this.needCurrency.invalid &&
      !this.haveAmount?.invalid;

    if (!baseValid) return false;

    if (this.isInstantMode) {
      return this.submitBehavior === 'navigate'
        ? !this.quoteLoading
        : !this.quoteLoading && this.hasUsableInstantQuote;
    }

    return this.orderForm.valid;
  }

  get submitText(): string {
    if (this.submissionPending) return 'Continuing…';
    if (this.submitBehavior === 'navigate') {
      return this.isInstantMode ? 'Continue to Instant Trade' : 'Continue with Flexible Order';
    }

    return this.isInstantMode ? 'Review Instant Trade' : 'Review Flexible Order';
  }

  ngOnInit(): void {
    this.configureHaveAmountValidators();
    this.bindFormChanges();
    this.ensureCurrencyDefaults();
    this.applyInitialDraftIfReady();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currencies']) {
      this.ensureCurrencyDefaults();
      this.applyInitialDraftIfReady();
    }

    if (changes['minimumHaveAmount'] || changes['maximumHaveAmount']) {
      this.configureHaveAmountValidators();
    }

    if (changes['initialDraft'] && !changes['initialDraft'].firstChange) {
      this.initialDraftApplied = false;
      this.applyInitialDraftIfReady();
    }

    if (changes['enableInstantQuotes'] && this.isInstantMode) {
      this.invalidateInstantQuote();
      this.scheduleInstantQuote();
    }

    if (changes['loading'] && changes['loading'].previousValue === true &&
      changes['loading'].currentValue === false) {
      if (this.isInstantMode) this.scheduleInstantQuote();
      setTimeout(() => this.tryAutoSubmit(), 0);
    }
  }

  ngOnDestroy(): void {
    this.stopQuoteCountdown();
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectTradeMode(mode: TradeExecutionMode): void {
    if (this.tradeMode === mode) {
      if (mode === 'instant') this.scheduleInstantQuote();
      return;
    }

    this.tradeMode = mode;
    this.invalidateInstantQuote();

    if (mode === 'instant') {
      this.scheduleInstantQuote();
    }
  }

  refreshInstantQuote(): void {
    this.invalidateInstantQuote();
    this.scheduleInstantQuote(true);
  }

  refreshInstantQuoteAndSubmit(): void {
    this.autoSubmitPending = true;
    this.refreshInstantQuote();
  }

  clearInstantQuote(): void {
    this.invalidateInstantQuote();
  }

  swapHaveNeed(): void {
    if (this.updating) return;
    this.updating = true;

    const haveCurrency = String(this.haveCurrency?.value ?? '');
    const needCurrency = String(this.needCurrency?.value ?? '');
    const haveAmount = this.format.toNumber(this.haveAmount?.value);
    const needAmount = this.format.toNumber(this.needAmount?.value);
    const oldPrice = this.format.toNumber(this.price?.value);
    const newPrice = oldPrice > 0 ? 1 / oldPrice : 0;

    this.orderForm.patchValue({
      haveCurrency: needCurrency,
      needCurrency: haveCurrency,
      haveAmount: this.format.formatPrice(needAmount, 2),
      needAmount: this.format.formatPrice(haveAmount, 2),
      price: newPrice > 0 ? this.format.formatPrice(newPrice) : 0
    }, { emitEvent: false });

    this.updating = false;
    this.emitPairChange();
    this.haveCurrencyChanged.emit(needCurrency);
    this.invalidateInstantQuote();
    this.scheduleInstantQuote();
  }

  submit(): void {
    this.markAllTouched();

    if (this.shouldRefreshInstantQuoteBeforeSubmit()) {
      this.refreshInstantQuoteAndSubmit();
      return;
    }

    if (!this.canSubmit) return;

    const raw = this.orderForm.getRawValue();
    const submission: OrderEntrySubmission = {
      draft: {
        haveCurrency: String(raw.haveCurrency),
        haveAmount: this.format.toNumber(raw.haveAmount),
        needCurrency: String(raw.needCurrency),
        needAmount: this.format.toNumber(raw.needAmount),
        price: this.format.toNumber(raw.price),
        executionMode: this.tradeMode
      },
      mode: this.tradeMode,
      quote: this.instantQuote
    };

    this.submissionPending = true;

    if (this.submitBehavior === 'navigate') {
      this.draftService.set(submission.draft);
      void this.router.navigate(['/orders'])
        .then(navigated => {
          if (!navigated) this.submissionPending = false;
        })
        .catch(() => {
          this.submissionPending = false;
        });
      return;
    }

    this.submitted.emit(submission);
    setTimeout(() => {
      this.submissionPending = false;
    }, 0);
  }


  private shouldRefreshInstantQuoteBeforeSubmit(): boolean {
    if (!this.isInstantMode || this.submitBehavior !== 'emit' || this.quoteLoading ||
      this.loading || this.blockedMessage || this.errorMessage ||
      this.submissionLocked || this.submissionPending) {
      return false;
    }

    const fromCurrency = String(this.haveCurrency?.value ?? '');
    const toCurrency = String(this.needCurrency?.value ?? '');
    const fromAmount = this.format.toNumber(this.haveAmount?.value);

    const validInput = !!fromCurrency && !!toCurrency &&
      fromCurrency !== toCurrency &&
      !this.haveCurrency?.invalid &&
      !this.needCurrency?.invalid &&
      !this.haveAmount?.invalid &&
      fromAmount > 0;

    return validInput && !this.hasUsableInstantQuote;
  }

  private bindFormChanges(): void {
    this.haveCurrency?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((haveCode: string) => {
        if (this.updating) return;

        const needControl = this.needCurrency;
        if (haveCode && needControl?.value === haveCode) {
          const fallback = this.currencies.find(currency => currency.currencyCode !== haveCode)?.currencyCode ?? '';
          this.updating = true;
          needControl.setValue(fallback, { emitEvent: false });
          this.updating = false;
        }

        this.haveCurrencyChanged.emit(haveCode);
        this.emitPairChange();
        this.invalidateInstantQuote();
        this.scheduleInstantQuote();
      });

    this.needCurrency?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((needCode: string) => {
        if (this.updating) return;

        const haveControl = this.haveCurrency;
        if (needCode && haveControl?.value === needCode) {
          const fallback = this.currencies.find(currency => currency.currencyCode !== needCode)?.currencyCode ?? '';
          this.updating = true;
          haveControl.setValue(fallback, { emitEvent: false });
          this.updating = false;
          this.haveCurrencyChanged.emit(fallback);
        }

        this.emitPairChange();
        this.invalidateInstantQuote();
        this.scheduleInstantQuote();
      });

    this.haveAmount?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.updating) return;

        if (this.isInstantMode) {
          this.invalidateInstantQuote();
          this.scheduleInstantQuote();
          return;
        }

        this.recalculatePrice(value, this.needAmount?.value);
      });

    this.needAmount?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.updating || this.isInstantMode) return;
        this.recalculatePrice(this.haveAmount?.value, value);
      });

    this.price?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.updating || this.isInstantMode) return;

        const rate = this.format.toNumber(value);
        const have = this.format.toNumber(this.haveAmount?.value);
        const need = this.format.toNumber(this.needAmount?.value);
        if (!rate || rate <= 0) return;

        this.updating = true;
        if (have > 0) {
          this.needAmount?.setValue(this.format.formatPrice(have * rate, 2), { emitEvent: false });
        } else if (need > 0) {
          this.haveAmount?.setValue(this.format.formatPrice(need / rate, 2), { emitEvent: false });
        }
        this.updating = false;
      });
  }

  private recalculatePrice(haveValue: unknown, needValue: unknown): void {
    const have = this.format.toNumber(haveValue);
    const need = this.format.toNumber(needValue);
    if (!have || have <= 0 || !need || need <= 0) return;

    this.updating = true;
    this.price?.setValue(this.format.formatPrice(need / have), { emitEvent: false });
    this.updating = false;
  }

  private ensureCurrencyDefaults(): void {
    if (!this.currencies.length || this.initialDraftApplied) return;

    const currentHave = String(this.haveCurrency?.value ?? '');
    const currentNeed = String(this.needCurrency?.value ?? '');
    const first = this.currencies[0]?.currencyCode ?? '';
    const second = this.currencies.find(currency => currency.currencyCode !== first)?.currencyCode ?? '';

    if (!currentHave || !this.currencies.some(currency => currency.currencyCode === currentHave)) {
      this.updating = true;
      this.haveCurrency?.setValue(first, { emitEvent: false });
      this.updating = false;
      this.haveCurrencyChanged.emit(first);
    }

    if (!currentNeed || currentNeed === this.haveCurrency?.value ||
      !this.currencies.some(currency => currency.currencyCode === currentNeed)) {
      this.updating = true;
      this.needCurrency?.setValue(second, { emitEvent: false });
      this.updating = false;
    }

    this.emitPairChange();
    this.scheduleInstantQuote();
  }

  private applyInitialDraftIfReady(): void {
    if (this.initialDraftApplied || !this.initialDraft || !this.currencies.length) return;

    const draft = this.initialDraft;
    this.tradeMode = draft.executionMode ?? 'flexible';
    this.updating = true;
    this.orderForm.patchValue({
      haveCurrency: draft.haveCurrency,
      haveAmount: Number(draft.haveAmount),
      needCurrency: draft.needCurrency,
      needAmount: Number(draft.needAmount),
      price: Number(draft.price)
    }, { emitEvent: false });
    this.updating = false;

    this.initialDraftApplied = true;
    this.autoSubmitPending = this.autoSubmitInitialDraft;
    this.haveCurrencyChanged.emit(draft.haveCurrency);
    this.emitPairChange();

    if (this.isInstantMode) {
      this.invalidateInstantQuote();
      this.scheduleInstantQuote(true);
    } else if (this.autoSubmitPending) {
      setTimeout(() => this.tryAutoSubmit(), 0);
    }
  }

  private emitPairChange(): void {
    const fromCurrency = String(this.haveCurrency?.value ?? '');
    const toCurrency = String(this.needCurrency?.value ?? '');
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return;
    this.pairChanged.emit({ fromCurrency, toCurrency });
  }

  private configureHaveAmountValidators(): void {
    if (!this.haveAmount) return;

    const validators = [
      Validators.required,
      Validators.min(Math.max(Number(this.minimumHaveAmount || 0.01), 0.01)),
      Validators.pattern(this.amountPattern)
    ];

    if (this.maximumHaveAmount !== null && this.maximumHaveAmount !== undefined) {
      validators.push(Validators.max(Math.max(Number(this.maximumHaveAmount), 0)));
    }

    this.haveAmount.setValidators(validators);
    this.haveAmount.updateValueAndValidity({ emitEvent: false });
  }

  private scheduleInstantQuote(immediate = false): void {
    if (!this.isInstantMode || !this.enableInstantQuotes) return;
    if (immediate) {
      this.requestInstantQuote();
      return;
    }
    this.quoteRequest$.next();
  }

  private requestInstantQuote(): void {
    if (!this.isInstantMode || !this.enableInstantQuotes || this.loading || this.blockedMessage) return;

    const fromCurrency = String(this.haveCurrency?.value ?? '').trim().toUpperCase();
    const toCurrency = String(this.needCurrency?.value ?? '').trim().toUpperCase();
    const fromAmount = this.format.toNumber(this.haveAmount?.value);

    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency || !fromAmount || fromAmount <= 0) {
      this.invalidateInstantQuote();
      return;
    }

    const requestSequence = ++this.quoteRequestSequence;
    this.quoteLoading = true;
    this.quoteRequestError = '';
    this.stopQuoteCountdown();

    this.houseTradeService.createInstantQuote({ fromCurrency, toCurrency, fromAmount })
      .pipe(finalize(() => {
        if (requestSequence === this.quoteRequestSequence) this.quoteLoading = false;
      }))
      .subscribe({
        next: response => {
          if (requestSequence !== this.quoteRequestSequence) return;

          this.instantQuote = response.data ?? null;
          if (this.instantQuote?.offeredRate && this.instantQuote?.expectedReceiveAmount) {
            this.updating = true;
            this.orderForm.patchValue({
              price: this.format.formatPrice(this.instantQuote.offeredRate),
              needAmount: this.format.formatPrice(this.instantQuote.expectedReceiveAmount, 2)
            }, { emitEvent: false });
            this.updating = false;
          } else {
            this.clearInstantCalculatedValues();
          }

          this.startQuoteCountdown();
          if (this.autoSubmitPending) {
            if (this.hasUsableInstantQuote) {
              setTimeout(() => this.tryAutoSubmit(), 0);
            } else {
              this.autoSubmitPending = false;
            }
          }
        },
        error: error => {
          if (requestSequence !== this.quoteRequestSequence) return;
          this.instantQuote = null;
          this.autoSubmitPending = false;
          this.clearInstantCalculatedValues();
          this.quoteRequestError = this.getApiErrorMessage(
            error,
            'Instant pricing is temporarily unavailable. You can place a flexible order instead.'
          );
        }
      });
  }

  private invalidateInstantQuote(): void {
    this.quoteRequestSequence++;
    this.instantQuote = null;
    this.quoteLoading = false;
    this.quoteRequestError = '';
    this.quoteExpiresInSeconds = 0;
    this.stopQuoteCountdown();

    if (this.isInstantMode) this.clearInstantCalculatedValues();
  }

  private clearInstantCalculatedValues(): void {
    this.updating = true;
    this.orderForm.patchValue({ needAmount: 0, price: 0 }, { emitEvent: false });
    this.updating = false;
  }

  private startQuoteCountdown(): void {
    this.stopQuoteCountdown();
    const expiresAt = this.instantQuote?.expiresAt;
    if (!expiresAt) return;

    const update = () => {
      const remainingMs = new Date(expiresAt).getTime() - Date.now();
      this.quoteExpiresInSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
      if (remainingMs <= 0) this.stopQuoteCountdown();
    };

    update();
    this.quoteCountdownTimer = setInterval(update, 1000);
  }

  private stopQuoteCountdown(): void {
    if (!this.quoteCountdownTimer) return;
    clearInterval(this.quoteCountdownTimer);
    this.quoteCountdownTimer = undefined;
  }

  private tryAutoSubmit(): void {
    if (!this.autoSubmitPending || !this.canSubmit) return;
    this.autoSubmitPending = false;
    this.submit();
  }

  private markAllTouched(): void {
    Object.values(this.orderForm.controls).forEach(control => control.markAsTouched());
    this.orderForm.updateValueAndValidity({ emitEvent: false });
  }

  private getApiErrorMessage(error: any, fallback: string): string {
    return error?.error?.error?.details ||
      error?.error?.Error?.Details ||
      error?.error?.message ||
      fallback;
  }
}
